import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Upload, Loader2, Sparkles } from "lucide-react";
import { API, formatApiErrorDetail } from "@/context/AuthContext";
import { useVisitorProfile } from "@/hooks/useVisitorProfile";

/**
 * Combined customer submission form — captures a review, a before/after pair,
 * or both in a single submission. No two-step process for the customer.
 */
export default function CustomerSubmissions() {
  const [form, setForm] = useState({ name: "", city: "", service: "", comment: "" });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { hydrate } = useVisitorProfile();

  // Pre-fill name from the visitor's saved profile if they've submitted a
  // quote on this device before.
  useEffect(() => {
    hydrate(setForm);
  }, [hydrate]);

  const reset = () => {
    setForm({ name: "", city: "", service: "", comment: "" });
    setRating(0);
    setBefore(null);
    setAfter(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please add your name");

    const hasReview = rating > 0;
    const hasBoth = !!(before && after);
    const hasOneOnly = !!before !== !!after;

    if (hasOneOnly) {
      return toast.error("Please add BOTH a before and an after photo, or skip both");
    }
    if (!hasReview && !hasBoth) {
      return toast.error("Add a star rating, photos, or both — at least one is required");
    }

    setSubmitting(true);
    try {
      const tasks = [];

      if (hasReview) {
        tasks.push(
          axios.post(`${API}/public/reviews`, {
            name: form.name.trim(),
            rating,
            comment: form.comment.trim() || undefined,
            city: form.city.trim() || undefined,
            service: form.service.trim() || undefined,
          })
        );
      }

      if (hasBoth) {
        const fd = new FormData();
        fd.append("name", form.name.trim());
        if (form.city.trim()) fd.append("city", form.city.trim());
        if (form.service.trim()) fd.append("service", form.service.trim());
        if (form.comment.trim()) fd.append("note", form.comment.trim());
        fd.append("before", before);
        fd.append("after", after);
        tasks.push(
          axios.post(`${API}/public/photo-submissions`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          })
        );
      }

      await Promise.all(tasks);

      if (hasReview && hasBoth) toast.success("Thanks! Your review and photos are in.");
      else if (hasReview) toast.success("Thanks! Your review is posted.");
      else toast.success("Thanks! Your before & after is in — we'll feature it soon.");

      reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const display = hover || rating;

  return (
    <section
      id="share"
      data-testid="customer-submissions"
      className="py-24 md:py-32 bg-stone-50"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">
            Customer corner
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 leading-tight" style={{ fontFamily: "Outfit" }}>
            Already a customer? Share the love.
          </h2>
          <p className="mt-6 text-lg text-stone-600 leading-relaxed">
            Drop a star rating, a before/after photo set, or both — one quick form, no account needed.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="customer-submission-form"
          className="bg-white rounded-3xl p-8 md:p-10 border border-stone-200 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-2xl bg-brand-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-brand-700" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-stone-900" style={{ fontFamily: "Outfit" }}>
                Share your experience
              </h3>
              <p className="text-sm text-stone-500">Review, photos, or both. Whatever you've got.</p>
            </div>
          </div>

          <div className="space-y-5">
            {/* Identity row */}
            <Field label="Your name" required>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="cs-name-input"
                className={INPUT}
                placeholder="Sarah T."
                autoComplete="name"
                required
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="City (optional)">
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="cs-city-input"
                  className={INPUT}
                  placeholder="Goodlettsville"
                  autoComplete="address-level2"
                />
              </Field>
              <Field label="Service (optional)">
                <input
                  type="text"
                  value={form.service}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}
                  data-testid="cs-service-input"
                  className={INPUT}
                  placeholder="Driveway"
                />
              </Field>
            </div>

            {/* Divider — Review block */}
            <SectionDivider label="Star rating + comment (optional)" />

            <Field label="Rating">
              <div className="flex items-center gap-1" data-testid="cs-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n === rating ? 0 : n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    data-testid={`cs-star-${n}`}
                    className="p-1 rounded-full hover:bg-brand-50 transition-all"
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  >
                    <Star className={`w-8 h-8 ${n <= display ? "text-brand-500 fill-brand-500" : "text-stone-300"}`} />
                  </button>
                ))}
                {rating > 0 && (
                  <button
                    type="button"
                    onClick={() => setRating(0)}
                    data-testid="cs-stars-clear"
                    className="ml-2 text-xs font-semibold text-stone-500 hover:text-stone-700"
                  >
                    clear
                  </button>
                )}
              </div>
            </Field>

            <Field label="Comment (optional)">
              <textarea
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                rows={3}
                data-testid="cs-comment-input"
                className={`${INPUT} resize-none`}
                placeholder="They showed up on time and the driveway looks brand new..."
              />
            </Field>

            {/* Divider — Photo block */}
            <SectionDivider label="Before & after photos (optional)" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FileInput label="Before photo" value={before} onChange={setBefore} testId="cs-before-input" />
              <FileInput label="After photo" value={after} onChange={setAfter} testId="cs-after-input" />
            </div>

            <p className="text-xs text-stone-500 leading-relaxed">
              Add a photo set OR a rating — one is enough. Add both for the best feature on our home page.
            </p>

            <button
              type="submit"
              disabled={submitting}
              data-testid="cs-submit-btn"
              className="w-full px-6 py-4 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white font-bold rounded-full inline-flex items-center justify-center gap-2 shadow-md transition-all hover:-translate-y-0.5"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {submitting ? "Sending..." : "Submit"}
            </button>
            <p className="text-xs text-stone-500 text-center">
              We review every submission. Great ones land on the home page within 24 hours.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}

const INPUT =
  "w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white focus:border-transparent transition-all text-sm";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-1.5 block">
        {label}
        {required && <span className="text-brand-700 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function SectionDivider({ label }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="h-px bg-stone-200 flex-1" />
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">{label}</span>
      <div className="h-px bg-stone-200 flex-1" />
    </div>
  );
}

function FileInput({ label, value, onChange, testId }) {
  return (
    <div className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-1.5 block">
        {label}
      </span>
      <label className="relative flex items-center justify-center gap-2 px-3 py-3 bg-stone-50 border border-dashed border-stone-300 hover:border-brand-300 hover:bg-brand-50/40 rounded-lg cursor-pointer transition-colors text-xs font-semibold text-stone-700 overflow-hidden">
        <Upload className="w-4 h-4" />
        <span className="truncate">{value ? value.name : "Choose photo"}</span>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
          data-testid={testId}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </label>
    </div>
  );
}
